<div class="py-20 bg-light">
  <div class="container mx-auto px-6 max-w-md">
    <div
      class="bg-white dark:bg-slate-800 p-8 rounded-xl shadow-lg dark:shadow-slate-900/50 border"
    >
      <h2 class="text-3xl font-bold text-secondary text-center">Connexion</h2>
      <p class="text-center text-gray-500 dark:text-gray-400 mt-2 mb-6">
        Accédez à votre dashboard pour analyser vos documents.
      </p>

      <!-- Message de succès -->
      @if (successMessage) {
        <div
          class="bg-green-100 dark:bg-green-900/30 border-l-4 border-green-500 text-green-700 dark:text-green-300 p-4 mb-6"
          role="alert"
        >
          <p>{{ successMessage }}</p>
        </div>
      }

      <!-- Message d'erreur -->
      @if (errorMessage) {
        <div
          class="bg-red-100 dark:bg-red-900/30 border-l-4 border-red-500 text-red-700 dark:text-red-300 p-4 mb-6"
          role="alert"
        >
          <p>{{ errorMessage }}</p>
        </div>
      }

      @if (twoFactorChallenge) {
        <form [formGroup]="codeForm" (ngSubmit)="onSubmitCode()">
          <p class="text-gray-700 dark:text-gray-300 mb-4">
            Double authentification activée : saisissez le code à 6 chiffres de
            votre application d'authentification, ou l'un de vos codes de
            secours.
          </p>
          <div class="mb-6">
            <label
              for="code"
              class="block text-gray-700 dark:text-gray-300 font-semibold mb-2"
              >Code</label
            >
            <input
              type="text"
              id="code"
              formControlName="code"
              autocomplete="one-time-code"
              maxlength="20"
              class="w-full px-4 py-2 border rounded-lg focus:outline-none focus:ring-2 focus:ring-primary text-center tracking-widest"
              placeholder="123456"
            />
            @if (
              codeForm.controls.code.invalid && codeForm.controls.code.touched
            ) {
              <p class="text-red-500 text-sm mt-1">Le code est requis.</p>
            }
          </div>
          <button
            type="submit"
            [disabled]="codeForm.invalid || isLoading"
            class="w-full bg-purple-600 text-white font-semibold text-lg py-3 rounded-lg hover:bg-purple-700 transition-colors shadow-md dark:shadow-slate-900/50 disabled:bg-purple-300 disabled:cursor-not-allowed"
          >
            {{ isLoading ? "Vérification..." : "Valider le code" }}
          </button>
          <button
            type="button"
            (click)="cancelTwoFactor()"
            class="w-full mt-3 text-primary hover:underline text-sm font-semibold"
          >
            Recommencer la connexion
          </button>
        </form>
      } @else {
        <!-- DÉTAILS: Le formulaire est lié au `loginForm` du composant via [formGroup]. -->
        <!-- (ngSubmit) déclenche la méthode `onSubmit` lors de la soumission. -->
        <form [formGroup]="loginForm" (ngSubmit)="onSubmit()">
          <div class="mb-4">
            <label
              for="email"
              class="block text-gray-700 dark:text-gray-300 font-semibold mb-2"
              >Email</label
            >
            <input
              type="email"
              id="email"
              formControlName="email"
              class="w-full px-4 py-2 border rounded-lg focus:outline-none focus:ring-2 focus:ring-primary"
              placeholder="vous@exemple.com"
            />

            <!-- Erreurs de validation email -->
            @if (
              loginForm.get("email")?.invalid && loginForm.get("email")?.touched
            ) {
              <p class="text-red-500 text-sm mt-1">
                @if (loginForm.get("email")?.errors?.["required"]) {
                  <span>L'email est requis.</span>
                }
                @if (loginForm.get("email")?.errors?.["email"]) {
                  <span>Veuillez entrer un email valide.</span>
                }
              </p>
            }
          </div>

          <div class="mb-6">
            <label
              for="password"
              class="block text-gray-700 dark:text-gray-300 font-semibold mb-2"
              >Mot de passe</label
            >
            <input
              type="password"
              id="password"
              formControlName="password"
              class="w-full px-4 py-2 border rounded-lg focus:outline-none focus:ring-2 focus:ring-primary"
              placeholder="********"
            />

            <!-- Erreurs de validation password -->
            @if (
              loginForm.get("password")?.invalid &&
              loginForm.get("password")?.touched
            ) {
              <p class="text-red-500 text-sm mt-1">
                @if (loginForm.get("password")?.errors?.["required"]) {
                  <span>Le mot de passe est requis.</span>
                }
              </p>
            }

            <!-- 🔐 Lien "Mot de passe oublié?" -->
            <p class="text-right mt-2">
              <a
                routerLink="/forgot-password"
                class="text-primary hover:underline text-sm font-semibold"
              >
                Mot de passe oublié ?
              </a>
            </p>
          </div>

          <button
            type="submit"
            [disabled]="loginForm.invalid || isLoading"
            class="w-full bg-purple-600 text-white font-semibold text-lg py-3 rounded-lg hover:bg-purple-700 transition-colors shadow-md dark:shadow-slate-900/50 disabled:bg-purple-300 disabled:cursor-not-allowed flex items-center justify-center"
          >
            @if (isLoading) {
              <svg
                class="animate-spin -ml-1 mr-3 h-5 w-5 text-white"
                xmlns="http://www.w3.org/2000/svg"
                fill="none"
                viewBox="0 0 24 24"
              >
                <circle
                  class="opacity-25"
                  cx="12"
                  cy="12"
                  r="10"
                  stroke="currentColor"
                  stroke-width="4"
                ></circle>
                <path
                  class="opacity-75"
                  fill="currentColor"
                  d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"
                ></path>
              </svg>
              <span>Connexion...</span>
            } @else {
              <span>Se connecter</span>
            }
          </button>
        </form>
      }

      <p class="text-center text-gray-600 dark:text-gray-400 mt-6">
        Pas encore de compte ?
        <a
          routerLink="/register"
          class="text-primary hover:underline font-semibold"
          >Inscrivez-vous</a
        >
      </p>
    </div>
  </div>
</div>
